/* Le Rendez-vous des Filles — scripts communs (vanilla, sans dépendance). */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const routes = window.theme.routes;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const store = {
    get(key, fallback) {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* stockage indisponible */ }
    },
  };

  /* ------------------------------------------------------------------
     Modales : tiroirs, dialogues (piège du focus, Échap, verrou du scroll)
     ------------------------------------------------------------------ */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const openModals = [];

  function openModal(el, { initialFocus, onClose } = {}) {
    if (!el || !el.hidden) return;
    const previous = document.activeElement;
    el.hidden = false;
    document.body.classList.add('is-locked');
    const entry = { el, previous, onClose };
    openModals.push(entry);
    el._modal = entry;
    requestAnimationFrame(() => {
      const target = (initialFocus && $(initialFocus, el)) || $(FOCUSABLE, el);
      if (target) target.focus({ preventScroll: true });
    });
  }

  function closeModal(el) {
    const entry = el && el._modal;
    if (!entry) return;
    el.hidden = true;
    el._modal = null;
    openModals.splice(openModals.indexOf(entry), 1);
    if (!openModals.length) document.body.classList.remove('is-locked');
    if (entry.onClose) entry.onClose();
    if (entry.previous && entry.previous.focus) entry.previous.focus({ preventScroll: true });
  }

  document.addEventListener('keydown', (e) => {
    const top = openModals[openModals.length - 1];
    if (!top) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal(top.el);
    } else if (e.key === 'Tab') {
      const items = $$(FOCUSABLE, top.el).filter((n) => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  document.addEventListener('click', (e) => {
    const closer = e.target.closest('[data-close]');
    if (closer) {
      const modal = closer.closest('.drawer, .dialog');
      if (modal) closeModal(modal);
    }
  });

  /* ------------------------------------------------------------------
     Toast
     ------------------------------------------------------------------ */
  const Toast = (() => {
    const el = $('[data-toast]');
    let timer;
    let handler;
    function show({ title, message = '', action = 'Voir le panier', onAction = () => Cart.open() }) {
      if (!el) return;
      $('[data-toast-title]', el).textContent = title;
      $('[data-toast-message]', el).textContent = message;
      const btn = $('[data-toast-action]', el);
      btn.textContent = action;
      handler = onAction;
      el.hidden = false;
      el.classList.remove('animate-fade-up');
      void el.offsetWidth;
      el.classList.add('animate-fade-up');
      clearTimeout(timer);
      timer = setTimeout(hide, action ? 5500 : 2800);
    }
    function hide() {
      if (el) el.hidden = true;
    }
    if (el) {
      $('[data-toast-action]', el).addEventListener('click', () => {
        hide();
        if (handler) handler();
      });
    }
    return { show, hide };
  })();

  /* ------------------------------------------------------------------
     Panier (API AJAX + Section Rendering)
     ------------------------------------------------------------------ */
  const Cart = (() => {
    const SECTION = 'cart-drawer';
    const drawer = () => $('[data-cart-drawer]');

    function render(sections) {
      if (!sections || !sections[SECTION]) return;
      const doc = new DOMParser().parseFromString(sections[SECTION], 'text/html');
      const fresh = $('[data-cart-drawer-inner]', doc);
      const current = $('[data-cart-drawer-inner]');
      if (fresh && current) current.replaceWith(fresh);
      const count = $('.drawer__title small', fresh);
      updateCount(count ? parseInt(count.textContent, 10) || 0 : 0);
      loadUpsell();
    }

    function updateCount(count) {
      $$('[data-cart-count]').forEach((n) => {
        n.textContent = count;
        n.hidden = count === 0;
      });
      $$('[data-cart-open]').forEach((n) => n.setAttribute('aria-label', `Mon panier, ${count} article(s)`));
    }

    async function request(url, body) {
      const d = drawer();
      if (d) d.classList.add('is-busy');
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: [SECTION], sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Erreur');
        render(data.sections);
        return data;
      } finally {
        if (d) d.classList.remove('is-busy');
      }
    }

    function add(items) {
      return request(`${routes.cartAdd}.js`, { items });
    }

    function change(key, quantity) {
      return request(`${routes.cartChange}.js`, { id: key, quantity });
    }

    function open() {
      const d = drawer();
      if (!d) return (window.location.href = routes.cart);
      openModal(d);
      loadUpsell();
    }

    let upsellFor = null;
    async function loadUpsell() {
      const box = $('[data-cart-upsell]');
      if (!box || !box.dataset.productId || drawer().hidden) return;
      const id = box.dataset.productId;
      if (upsellFor === id && window._upsellHtml) {
        box.innerHTML = window._upsellHtml;
        return;
      }
      upsellFor = id;
      try {
        const res = await fetch(`${routes.root}recommendations/products?product_id=${id}&limit=2&section_id=cart-upsell`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = $('[data-upsell-inner]', doc);
        window._upsellHtml = inner ? inner.innerHTML : '';
        const current = $('[data-cart-upsell]');
        if (current) current.innerHTML = window._upsellHtml;
        Wishlist.sync();
      } catch (e) { /* pas de suggestions */ }
    }

    document.addEventListener('click', async (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (opener) {
        e.preventDefault();
        open();
        return;
      }

      const qtyBtn = e.target.closest('[data-cart-qty]');
      if (qtyBtn) {
        change(qtyBtn.dataset.cartQty, parseInt(qtyBtn.dataset.qty, 10)).catch((err) => Toast.show({ title: 'Oups', message: err.message, action: '' }));
        return;
      }

      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        const line = removeBtn.closest('[data-key]');
        const name = $('.cart-line__name', line)?.textContent || '';
        const before = await fetch(`${routes.cart}.js`).then((r) => r.json());
        const item = before.items.find((i) => i.key === removeBtn.dataset.cartRemove);
        await change(removeBtn.dataset.cartRemove, 0);
        if (item) {
          Toast.show({
            title: 'Article retiré',
            message: name,
            action: 'Annuler',
            onAction: () => add([{ id: item.variant_id, quantity: item.quantity, properties: item.properties || {} }]),
          });
        }
        return;
      }

      const quick = e.target.closest('[data-quick-add]');
      if (quick) {
        quick.disabled = true;
        try {
          await add([{ id: Number(quick.dataset.quickAdd), quantity: 1 }]);
          Toast.show({ title: 'Ajouté au panier', message: quick.dataset.productTitle || '' });
        } catch (err) {
          Toast.show({ title: 'Impossible d’ajouter cet article', message: err.message, action: '' });
        } finally {
          quick.disabled = false;
        }
      }
    });

    // Tout lien qui mène au panier depuis le tiroir de suggestions ferme le tiroir.
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-drawer] a[href]') && !e.target.closest('[data-close]')) {
        const d = drawer();
        if (d && !d.hidden) closeModal(d);
      }
    });

    return { add, change, open, render, updateCount };
  })();

  /* ------------------------------------------------------------------
     Favoris / listes d'envies (localStorage)
     ------------------------------------------------------------------ */
  const Wishlist = (() => {
    const KEY = 'lrdf:wishlists';
    const DEFAULT = { id: 'mes-envies', name: 'Mes envies', isDefault: true, handles: [] };
    let lists = load();

    function load() {
      const saved = store.get(KEY, null);
      if (!Array.isArray(saved) || !saved.length) return [{ ...DEFAULT, createdAt: Date.now() }];
      if (!saved.some((l) => l.isDefault)) saved.unshift({ ...DEFAULT, createdAt: Date.now() });
      return saved.map((l) => ({ ...l, handles: Array.isArray(l.handles) ? l.handles : [] }));
    }
    function save() {
      store.set(KEY, lists);
      sync();
      document.dispatchEvent(new CustomEvent('wishlist:change'));
    }
    const all = () => [...new Set(lists.flatMap((l) => l.handles))];
    const has = (handle) => lists.some((l) => l.handles.includes(handle));
    const cleanName = (name) => name.replace(/\s+/g, ' ').trim().slice(0, 32);

    function toggle(handle) {
      if (has(handle)) {
        lists.forEach((l) => (l.handles = l.handles.filter((h) => h !== handle)));
      } else {
        const def = lists.find((l) => l.isDefault);
        def.handles = [handle, ...def.handles];
      }
      save();
      return has(handle);
    }

    function sync() {
      const count = all().length;
      $$('[data-wishlist-count]').forEach((n) => {
        n.textContent = count;
        n.hidden = count === 0;
      });
      $$('[data-wishlist-link]').forEach((n) => n.setAttribute('aria-label', `Mes favoris, ${count} pièce(s)`));
      $$('[data-wishlist-toggle]').forEach((btn) => {
        const on = has(btn.dataset.wishlistToggle);
        btn.setAttribute('aria-pressed', String(on));
        const label = on ? btn.dataset.labelRemove : btn.dataset.labelAdd;
        if (label) btn.setAttribute('aria-label', label);
      });
    }

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      toggle(btn.dataset.wishlistToggle);
    });

    window.addEventListener('storage', (e) => {
      if (e.key === KEY) {
        lists = load();
        sync();
        document.dispatchEvent(new CustomEvent('wishlist:change'));
      }
    });

    return {
      get lists() { return lists; },
      all,
      has,
      toggle,
      sync,
      create(name) {
        const clean = cleanName(name);
        if (!clean) return null;
        const existing = lists.find((l) => l.name.toLowerCase() === clean.toLowerCase());
        if (existing) return existing;
        const list = { id: `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`, name: clean, isDefault: false, handles: [], createdAt: Date.now() };
        lists.push(list);
        save();
        return list;
      },
      rename(id, name) {
        const list = lists.find((l) => l.id === id);
        const clean = cleanName(name);
        if (list && !list.isDefault && clean) {
          list.name = clean;
          save();
        }
      },
      remove(id) {
        lists = lists.filter((l) => l.id !== id || l.isDefault);
        save();
      },
      clear(id) {
        const list = lists.find((l) => l.id === id);
        if (list) {
          list.handles = [];
          save();
        }
      },
      removeFrom(id, handle) {
        const list = lists.find((l) => l.id === id);
        if (list) {
          list.handles = list.handles.filter((h) => h !== handle);
          save();
        }
      },
      setMembership(handle, ids) {
        lists.forEach((l) => {
          const inList = l.handles.includes(handle);
          if (ids.includes(l.id) && !inList) l.handles = [handle, ...l.handles];
          if (!ids.includes(l.id) && inList) l.handles = l.handles.filter((h) => h !== handle);
        });
        save();
      },
    };
  })();

  /* ------------------------------------------------------------------
     En-tête : méga-menu, menu mobile, recherche
     ------------------------------------------------------------------ */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;

    // Méga-menu
    let closeTimer;
    let openId = null;
    const setMega = (id) => {
      clearTimeout(closeTimer);
      openId = id;
      $$('[data-mega]', header).forEach((panel) => {
        const on = panel.id === id;
        panel.classList.toggle('is-open', on);
        panel.setAttribute('aria-hidden', String(!on));
        $$('a', panel).forEach((a) => (on ? a.removeAttribute('tabindex') : a.setAttribute('tabindex', '-1')));
      });
      $$('[data-mega-trigger]', header).forEach((t) => t.setAttribute('aria-expanded', String(t.dataset.megaTrigger === id)));
    };
    const scheduleClose = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => setMega(null), 120);
    };
    $$('[data-mega-trigger]', header).forEach((t) => {
      t.addEventListener('mouseenter', () => setMega(t.dataset.megaTrigger));
      t.addEventListener('focus', () => setMega(t.dataset.megaTrigger));
    });
    $$('[data-mega-close]', header).forEach((t) => {
      t.addEventListener('mouseenter', scheduleClose);
      t.addEventListener('focus', () => setMega(null));
    });
    $$('[data-mega]', header).forEach((panel) => {
      panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      panel.addEventListener('mouseleave', scheduleClose);
      panel.addEventListener('click', (e) => e.target.closest('a') && setMega(null));
    });
    header.addEventListener('mouseleave', scheduleClose);
    document.addEventListener('keydown', (e) => e.key === 'Escape' && openId && setMega(null));

    // Menu mobile
    const mobile = $('[data-mobile-menu]');
    const burger = $('[data-mobile-menu-open]', header);
    if (mobile && burger) {
      burger.addEventListener('click', () => {
        burger.setAttribute('aria-expanded', 'true');
        openModal(mobile, { onClose: () => burger.setAttribute('aria-expanded', 'false') });
      });
      mobile.addEventListener('click', (e) => e.target.closest('a[href]') && closeModal(mobile));
    }

    // Recherche prédictive
    const overlay = $('[data-search]', header);
    const openBtn = $('[data-search-open]', header);
    if (!overlay || !openBtn) return;
    const input = $('[data-search-input]', overlay);
    const form = $('[data-search-form]', overlay);
    const results = $('[data-search-results]', overlay);
    const recentBox = $('[data-search-recent]', overlay);
    const recentList = $('[data-search-recent-list]', overlay);
    const RECENT = 'lrdf:recent-searches';
    const normalize = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

    const renderRecent = () => {
      const items = store.get(RECENT, []);
      recentBox.hidden = !!input.value.trim() || !items.length;
      recentList.innerHTML = '';
      items.forEach((term) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'chip';
        b.innerHTML = '<svg class="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>';
        b.append(document.createTextNode(term));
        b.addEventListener('click', () => {
          input.value = term;
          submit();
        });
        recentList.append(b);
      });
    };
    const saveRecent = (term) => {
      const t = term.trim();
      if (!t) return;
      const items = store.get(RECENT, []).filter((x) => normalize(x) !== normalize(t));
      store.set(RECENT, [t, ...items].slice(0, 4));
    };
    const submit = () => {
      saveRecent(input.value);
      form.submit();
    };
    const close = () => {
      overlay.hidden = true;
      openBtn.setAttribute('aria-expanded', 'false');
    };
    openBtn.addEventListener('click', () => {
      if (!overlay.hidden) return close();
      setMega(null);
      overlay.hidden = false;
      openBtn.setAttribute('aria-expanded', 'true');
      renderRecent();
      input.focus();
    });
    $('[data-search-close]', overlay).addEventListener('click', close);
    $('[data-search-clear]', overlay).addEventListener('click', () => {
      store.set(RECENT, []);
      renderRecent();
    });
    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        close();
        openBtn.focus();
      }
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (input.value.trim()) submit();
    });
    results.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-submit]')) submit();
      else if (e.target.closest('[data-search-save]')) saveRecent(input.value);
    });

    let debounce;
    let controller;
    input.addEventListener('input', () => {
      renderRecent();
      clearTimeout(debounce);
      const q = input.value.trim();
      if (!q) {
        results.innerHTML = '';
        return;
      }
      debounce = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        const params = new URLSearchParams({
          q,
          section_id: 'predictive-search',
          'resources[type]': 'product,collection',
          'resources[limit]': '5',
          'resources[options][prefix]': 'last',
        });
        try {
          const res = await fetch(`${routes.predictiveSearch}?${params}`, { signal: controller.signal });
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = $('.shopify-section', doc) || doc.body;
          results.innerHTML = section.innerHTML;
        } catch (err) { /* requête annulée */ }
      }, 200);
    });
  }

  /* ------------------------------------------------------------------
     Apparition douce des sections
     ------------------------------------------------------------------ */
  function initReveal() {
    const items = $$('[data-reveal]');
    if (!items.length || reducedMotion || !('IntersectionObserver' in window)) return;
    const reveal = (el) => el.setAttribute('data-reveal', 'in');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) {
          reveal(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) return;
      el.setAttribute('data-reveal', 'pending');
      io.observe(el);
    });
    const safety = setInterval(() => {
      const pending = $$('[data-reveal="pending"]');
      if (!pending.length) return clearInterval(safety);
      pending.forEach((el) => el.getBoundingClientRect().top < window.innerHeight && reveal(el));
    }, 1000);
  }

  window.LRDF = { $, $$, store, openModal, closeModal, Toast, Cart, Wishlist };

  const boot = () => {
    initHeader();
    initReveal();
    Wishlist.sync();
    // Rendu de sections dans l'éditeur de thème
    document.addEventListener('shopify:section:load', () => {
      Wishlist.sync();
      initReveal();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
